<template>
  <div class="fin-page">
    <div class="fin-hero">
      <div class="fin-hero-left">
        <div class="fin-hero-icon"><i class="fa fa-file-invoice-dollar"></i></div>
        <div>
          <h1>Gjendja analitike</h1>
          <p>Gjendja e stokut ashtu si e raporton Financa, për çdo magazinë dhe produkt. Gjendja përditësohet çdo 5 minuta; furnizimet mbyllen një herë në ditë (neti i ditës).</p>
        </div>
      </div>

      <div class="fin-hero-right">
        <span class="fin-sync-badge" :class="syncBadgeClass">
          <i class="fa" :class="lastSync ? 'fa-clock' : 'fa-triangle-exclamation'"></i>
          Përditësuar {{ formatRelative(lastSync && lastSync.finished_at) }}
        </span>
        <button v-if="canSync" type="button" class="fin-btn" :disabled="syncing" @click="syncNow">
          <i class="fa" :class="syncing ? 'fa-circle-notch fa-spin' : 'fa-rotate'"></i>
          Rifresko nga financa
        </button>
        <button v-if="canExport" type="button" class="fin-btn" :disabled="exporting || loading" @click="exportCurrentView">
          <i class="fa" :class="exporting ? 'fa-circle-notch fa-spin' : 'fa-download'"></i>
          Eksporto
        </button>
        <button v-if="canImport && tab !== 'dashboard'" type="button" class="fin-btn primary" @click="showImportModal = true">
          <i class="fa fa-upload"></i>
          Importo skedar
        </button>
        <button type="button" class="fin-btn" :disabled="loading" @click="reload">
          <i class="fa fa-arrows-rotate"></i>
          Rifresko pamjen
        </button>
      </div>
    </div>

    <div v-if="message" class="fin-warn" :class="{ 'is-info': messageType === 'info' }">
      <i class="fa" :class="messageType === 'info' ? 'fa-circle-check' : 'fa-circle-info'" style="margin-top: 2px"></i>
      <span>{{ message }}</span>
      <button type="button" class="fin-link-btn" style="margin-left: auto" @click="message = ''">Mbyll</button>
    </div>

    <div v-if="matchNote" class="fin-warn is-info">
      <i class="fa fa-circle-info" style="margin-top: 2px"></i>
      <span>{{ matchNote }}</span>
    </div>

    <div v-if="unmatchedNote" class="fin-warn">
      <i class="fa fa-triangle-exclamation" style="margin-top: 2px"></i>
      <div class="fin-warn-body">
        <div>{{ unmatchedNote }}</div>
        <button type="button" class="fin-btn" style="margin-top: 8px" @click="showUnmatchedModal = true">
          <i class="fa fa-list"></i>
          Shiko dhe shkarko kodet
        </button>
      </div>
    </div>

    <div class="fin-tabs">
      <button type="button" class="fin-tab" :class="{ active: tab === 'stock' }" @click="switchTab('stock')">Gjendja</button>
      <button v-if="canSeeMovements" type="button" class="fin-tab" :class="{ active: tab === 'supply' }" @click="switchTab('supply')">Furnizime</button>
      <button type="button" class="fin-tab" :class="{ active: tab === 'dashboard' }" @click="switchTab('dashboard')">Përmbledhje</button>
    </div>

    <financa-filters
      v-if="tab !== 'dashboard'"
      v-model="filters"
      :warehouses="warehouses"
      :points-of-sale="pointsOfSale"
      :categories="categories"
      :facilities="facilities"
      :fushat="fushat"
      :campaigns="campaigns"
      :suppliers="suppliers"
      :vitrine-facility-id="vitrineFacilityId"
      :show-as-of="tab === 'stock'"
    />

    <div v-if="tab === 'stock'" class="fin-summary-row">
      <div class="fin-summary-card" :class="{ 'is-loading': loading || totalsLoading }">
        <div class="fin-summary-icon"><i class="fa fa-list"></i></div>
        <div>
          <div class="fin-summary-value">
            <span v-if="totalsLoading || (loading && stockData.pagination.total == null)" class="fin-skeleton-line fin-skeleton-line--sm"></span>
            <template v-else>{{ stockData.pagination.total != null ? stockData.pagination.total : "…" }}</template>
          </div>
          <div class="fin-summary-label">Rreshta / produkte të shfaqura</div>
        </div>
      </div>
      <div class="fin-summary-card" :class="{ 'is-loading': loading || totalsLoading }">
        <div class="fin-summary-icon"><i class="fa fa-cubes"></i></div>
        <div>
          <div class="fin-summary-value">
            <span v-if="totalsLoading || (loading && stockData.totals.qty == null)" class="fin-skeleton-line fin-skeleton-line--md"></span>
            <template v-else>{{ formatQty(stockData.totals.qty) }}</template>
          </div>
          <div class="fin-summary-label">Sasia totale në filtrat aktualë</div>
        </div>
      </div>
      <div class="fin-summary-card" :class="{ 'is-loading': loading || totalsLoading }">
        <div class="fin-summary-icon"><i class="fa fa-coins"></i></div>
        <div>
          <div class="fin-summary-value">
            <span v-if="totalsLoading || (loading && stockData.totals.value == null)" class="fin-skeleton-line fin-skeleton-line--md"></span>
            <template v-else>{{ formatValue(stockData.totals.value) }}</template>
          </div>
          <div class="fin-summary-label">Vlera totale ({{ valueSourceLabel }})</div>
        </div>
      </div>
    </div>

    <template v-if="tab === 'stock'">
      <financa-stock-table
        :columns="stockData.columns"
        :rows="stockData.rows"
        :totals="stockData.totals"
        :pagination="stockData.pagination"
        :metric="filters.metric"
        :loading="loading"
        :sort="filters.sort"
        :sort-dir="filters.sort_dir"
        :empty-hint="stockEmptyHint"
        :can-open-product="canEditProduct"
        @page="changePage"
        @per-page="changePerPage"
        @sort="changeSort"
        @open-history="openHistory"
        @open-product="openProductKartela"
      />
    </template>

    <financa-supply-view
      v-else-if="tab === 'supply'"
      :columns="movementData.columns"
      :rows="movementData.rows"
      :totals="movementData.totals"
      :pagination="movementData.pagination"
      :range="range"
      :direction="direction"
      :min-increase="filters.min_increase"
      :loading="loading"
      :totals-loading="totalsLoading"
      :can-open-product="canEditProduct"
      @update:range="onRangeChange"
      @update:direction="onDirectionChange"
      @update:minIncrease="onMinIncreaseChange"
      @page="changePage"
      @per-page="changePerPage"
      @open-history="openHistory"
      @open-product="openProductKartela"
    />

    <financa-dashboard
      v-else
      :groups="dashboardData.groups"
      :totals="dashboardData.totals"
      :expanded="dashboardExpanded"
      :expanded-guid="dashboardExpandGuid"
      :loading="loading"
      :expand-loading="dashboardExpandLoading"
      :value-label="valueSourceLabel"
      :can-open-product="canEditProduct"
      @expand="toggleDashboardExpand"
      @expand-page="onDashboardExpandPage"
      @expand-per-page="onDashboardExpandPerPage"
      @open-product="openProductKartela"
    />

    <financa-product-history-drawer v-if="historyProduct" :product="historyProduct" :filters="filters" :range="tab === 'supply' ? range : null" @close="historyProduct = null" />

    <financa-import-modal v-if="showImportModal" @close="showImportModal = false" @apply="applyImport" />

    <financa-unmatched-modal
      v-if="showUnmatchedModal"
      :products="unmatchedProductRows"
      :warehouses="unmatchedWarehouseRows"
      @close="showUnmatchedModal = false"
    />

    <div v-if="formEdit" class="fin-drawer-backdrop" @click.self="closeProductKartela">
      <div class="fin-drawer fin-drawer--wide">
        <div class="fin-drawer-head">
          <div>
            <div class="fin-drawer-title">Kartela e produktit</div>
            <div class="fin-drawer-sub">Modifikim nga Gjendja analitike</div>
          </div>
          <button type="button" class="fin-btn" @click="closeProductKartela"><i class="fa fa-times"></i> Mbyll</button>
        </div>
        <div class="fin-drawer-body">
          <div v-if="formEdit.loading" class="fin-loading"><i class="fa fa-circle-notch fa-spin"></i> Duke hapur kartelën...</div>
          <DaForm v-else :form="formEdit" :closes_form="true" :saving="productSaving" @submitted="submitProductKartela" @close="closeProductKartela" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FinancaStockService from "@/services/FinancaStock";
import FinancaFilters from "./components/FinancaFilters.vue";
import FinancaStockTable from "./components/FinancaStockTable.vue";
import FinancaSupplyView from "./components/FinancaSupplyView.vue";
import FinancaDashboard from "./components/FinancaDashboard.vue";
import FinancaProductHistoryDrawer from "./components/FinancaProductHistoryDrawer.vue";
import FinancaImportModal from "./components/FinancaImportModal.vue";
import FinancaUnmatchedModal from "./components/FinancaUnmatchedModal.vue";
import DaForm from "@/components/daform/index.vue";
import editProduct from "@/pages/products/forms/product form/edit";
import ProductsServices from "@/services/Product";
import formatMixin from "./shared/formatMixin.js";
import permissionMixin from "@/mixins/permissions-mixin.js";
import "./shared/pageStyles.css";

const emptyGrid = () => ({
  columns: [],
  rows: [],
  totals: {},
  pagination: { page: 1, per_page: 50, total: null, last_page: null, has_more: false, row_count: 0 },
});

export default {
  name: "GjendjaSipasFinances",
  mixins: [permissionMixin, formatMixin],
  components: { FinancaFilters, FinancaStockTable, FinancaSupplyView, FinancaDashboard, FinancaProductHistoryDrawer, FinancaImportModal, FinancaUnmatchedModal, DaForm },
  data() {
    const to = new Date();
    const from = new Date(to.getTime() - 24 * 3600 * 1000);

    return {
      tab: "stock",
      loading: false,
      syncing: false,
      exporting: false,
      showImportModal: false,
      showUnmatchedModal: false,
      message: "",
      messageType: "error",
      warehouses: [],
      pointsOfSale: [],
      categories: [],
      facilities: [],
      fushat: [],
      campaigns: [],
      suppliers: [],
      vitrineFacilityId: 9,
      status: null,
      lastSync: null,
      valueSourceFromApi: null,
      stockData: emptyGrid(),
      movementData: emptyGrid(),
      dashboardData: { groups: [], totals: {} },
      dashboardExpandGuid: null,
      dashboardExpanded: null,
      dashboardExpandLoading: false,
      historyProduct: null,
      formEdit: null,
      productSaving: false,
      loadTimer: null,
      requestSeq: 0,
      totalsSeq: 0,
      lastPayloadKey: null,
      totalsLoading: false,
      direction: "in",
      range: {
        from: this.formatForApi(from),
        to: this.formatForApi(to),
      },
      filters: {
        group_by: "pos",
        pos_ids: [],
        warehouse_ids: [],
        product_guids: [],
        product_category_guids: [],
        accessory_category_guids: [],
        warehouse_group_guids: [],
        supplier_guids: [],
        warehouse_facility_id: null,
        fushat_guid: null,
        campaign_guid: null,
        web_available: false,
        has_offer: false,
        vitrine_split: false,
        as_of: null,
        min_increase: 10,
        search: "",
        stock_filter: "with",
        metric: "qty",
        page: 1,
        per_page: 50,
        sort: "name",
        sort_dir: "asc",
      },
    };
  },
  computed: {
    canSeeMovements() {
      return this.am_allowed("stock_financa.can_see_movements");
    },
    canSync() {
      return this.am_allowed("stock_financa.can_sync");
    },
    canImport() {
      return this.am_allowed("stock_financa.can_import");
    },
    canExport() {
      return this.am_allowed("stock_financa.can_export");
    },
    canEditProduct() {
      return this.am_allowed("product.can_edit_product");
    },
    syncBadgeClass() {
      if (!this.lastSync) return "is-failed";
      const finished = this.parseServerDate(this.lastSync.finished_at);
      if (isNaN(finished.getTime())) return "is-stale";
      return Date.now() - finished.getTime() > 20 * 60 * 1000 ? "is-stale" : "";
    },
    valueSourceLabel() {
      return (this.valueSourceFromApi && this.valueSourceFromApi.label) || "sasia × kosto (KOSTO nga Financa)";
    },
    stockEmptyHint() {
      if (this.filters.stock_filter === "with") {
        return "Asnjë produkt me stok. Nëse sasitë janë 0, zgjidh «Të gjitha» ose «Pa stok» te filtri Stok.";
      }
      if (this.filters.web_available) {
        return "Asnjë produkt i shënuar për web në magazinat web, me këta filtra.";
      }
      if (this.filters.has_offer) {
        return "Asnjë produkt me cmim shitje më të ulët se cmimi bazë, me këta filtra.";
      }
      return "Nuk u gjet asnjë produkt me këto filtra.";
    },
    matchNote() {
      if (!this.status || !this.status.fetched) return "";
      const fetched = Number(this.status.fetched.products || 0);
      if (!fetched) return "";
      const matched = Number((this.status.matched && this.status.matched.products) || 0);
      const whFetched = Number(this.status.fetched.warehouses || 0);
      const whMatched = Number((this.status.matched && this.status.matched.warehouses) || 0);
      return matched + " nga " + fetched + " produkte u përputhën" + (whFetched ? " (" + whMatched + " nga " + whFetched + " magazina)." : ".");
    },
    unmatchedNote() {
      if (!this.status || !this.status.unmatched) return "";
      const { products, warehouses } = this.status.unmatched;
      if (!products && !warehouses) return "";

      const parts = [];
      if (products) parts.push(products + " kode produkti");
      if (warehouses) parts.push(warehouses + " kode magazine");
      return "Financa raporton " + parts.join(" dhe ") + " që nuk gjenden në sistem, prandaj nuk shfaqen në tabelë.";
    },
    unmatchedProductRows() {
      return this.normalizeUnmatchedList(this.status && this.status.unmatched && this.status.unmatched.product_codes);
    },
    unmatchedWarehouseRows() {
      return this.normalizeUnmatchedList(this.status && this.status.unmatched && this.status.unmatched.warehouse_codes);
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        if (this.tab === "dashboard") return;
        const key = this.payloadKey();
        if (key === this.lastPayloadKey) return;
        this.scheduleLoad();
      },
    },
    "$route.query.tab": {
      immediate: false,
      handler(tab) {
        if (tab === "supply" && this.canSeeMovements && this.tab !== "supply") {
          this.switchTab("supply");
        }
        if (tab === "dashboard" && this.tab !== "dashboard") {
          this.switchTab("dashboard");
        }
      },
    },
  },
  beforeUnmount() {
    if (this.loadTimer) clearTimeout(this.loadTimer);
  },
  async created() {
    if (!this.am_allowed("stock_financa.can_see")) {
      window.location.href = "/home";
      return;
    }

    if (this.$route.query.tab === "supply" && this.canSeeMovements) {
      this.tab = "supply";
    } else if (this.$route.query.tab === "dashboard") {
      this.tab = "dashboard";
    }

    await this.loadFilters();
    await this.loadStatus();
    await this.load();
  },
  methods: {
    formatForApi(date) {
      const pad = (n) => String(n).padStart(2, "0");
      return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate()) + " " + pad(date.getHours()) + ":" + pad(date.getMinutes()) + ":00";
    },

    payloadKey() {
      const f = { ...this.filters };
      delete f.metric;
      return JSON.stringify({
        tab: this.tab,
        filters: f,
        range: this.range,
        direction: this.direction,
      });
    },

    scheduleLoad() {
      if (this.loadTimer) clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => {
        this.loadTimer = null;
        this.load();
      }, 250);
    },

    async loadFilters() {
      try {
        const data = await FinancaStockService.getFilters();
        const asList = (value) => (Array.isArray(value) ? value : value && typeof value === "object" ? Object.values(value) : []);
        this.warehouses = asList(data.warehouses);
        this.pointsOfSale = asList(data.points_of_sale);
        this.categories = asList(data.categories);
        this.facilities = asList(data.facilities);
        this.fushat = asList(data.fushat);
        this.campaigns = asList(data.campaigns);
        this.suppliers = asList(data.suppliers);
        this.vitrineFacilityId = data.vitrine_facility_id || 9;
        if (data.value_source) this.valueSourceFromApi = data.value_source;
      } catch (error) {
        this.notify("Nuk u ngarkuan dot filtrat: " + this.errorText(error));
      }
    },

    async loadStatus() {
      try {
        this.status = await FinancaStockService.getStatus();
        this.lastSync = this.status.last_sync;
        if (this.status.value_source) this.valueSourceFromApi = this.status.value_source;
      } catch (error) {
        this.status = null;
      }
    },

    notify(text, type = "error") {
      this.message = text;
      this.messageType = type;
    },

    normalizeUnmatchedList(list) {
      if (!Array.isArray(list)) return [];
      return list
        .map((item) => {
          if (item == null) return null;
          if (typeof item === "string") return { code: item, rows: 0, qty: 0 };
          const code = String(item.code || "").trim();
          if (!code) return null;
          return { code, rows: Number(item.rows || 0), qty: Number(item.qty || 0) };
        })
        .filter(Boolean);
    },

    async load() {
      const key = this.payloadKey();
      this.lastPayloadKey = key;
      const seq = ++this.requestSeq;
      this.loading = true;
      this.totalsLoading = false;
      if (this.messageType === "error") this.message = "";

      try {
        const payload = { ...this.filters };

        if (this.tab === "supply") {
          const data = await FinancaStockService.getMovements({
            ...payload,
            from: this.range.from,
            to: this.range.to,
            direction: this.direction,
            include_totals: false,
          });
          if (seq !== this.requestSeq) return;
          this.movementData = this.normalize(data);
          this.lastSync = data.last_sync || this.lastSync;
          this.loading = false;
          this.loadTotalsChunk(seq, "supply", payload);
        } else if (this.tab === "dashboard") {
          await this.loadDashboard();
          if (seq !== this.requestSeq) return;
        } else {
          const data = await FinancaStockService.getStock({ ...payload, include_totals: false });
          if (seq !== this.requestSeq) return;
          this.stockData = this.normalize(data);
          this.lastSync = data.last_sync || this.lastSync;
          if (data.value_source) this.valueSourceFromApi = data.value_source;
          this.loading = false;
          this.loadTotalsChunk(seq, "stock", payload);
        }
      } catch (error) {
        if (seq !== this.requestSeq) return;
        this.notify(this.errorText(error));
        if (this.tab === "supply") this.movementData = emptyGrid();
        else if (this.tab === "dashboard") this.dashboardData = { groups: [], totals: {} };
        else this.stockData = emptyGrid();
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },

    async loadTotalsChunk(pageSeq, tab, payload) {
      if (pageSeq !== this.requestSeq) return;
      const tSeq = ++this.totalsSeq;
      this.totalsLoading = true;
      try {
        let data;
        if (tab === "supply") {
          data = await FinancaStockService.getMovementTotals({
            ...payload,
            from: this.range.from,
            to: this.range.to,
            direction: this.direction,
          });
          if (pageSeq !== this.requestSeq || tSeq !== this.totalsSeq) return;
          this.movementData = {
            ...this.movementData,
            totals: { ...this.movementData.totals, ...data.totals, pending: false },
            pagination: {
              ...this.movementData.pagination,
              ...(data.pagination || {}),
              row_count: (this.movementData.rows || []).length,
            },
          };
        } else {
          data = await FinancaStockService.getStockTotals(payload);
          if (pageSeq !== this.requestSeq || tSeq !== this.totalsSeq) return;
          this.stockData = {
            ...this.stockData,
            totals: { ...this.stockData.totals, ...data.totals, pending: false },
            pagination: {
              ...this.stockData.pagination,
              ...(data.pagination || {}),
              row_count: (this.stockData.rows || []).length,
            },
          };
          if (data.value_source) this.valueSourceFromApi = data.value_source;
        }
      } catch (error) {
        if (pageSeq !== this.requestSeq || tSeq !== this.totalsSeq) return;
        console.warn("Financa totals chunk failed", error);
      } finally {
        if (pageSeq === this.requestSeq && tSeq === this.totalsSeq) this.totalsLoading = false;
      }
    },

    async loadDashboard(expandGuid = this.dashboardExpandGuid) {
      const payload = {
        warehouse_group_guids: this.filters.warehouse_group_guids,
        web_available: this.filters.web_available,
        as_of: this.filters.as_of,
        page: this.filters.page,
        per_page: this.filters.per_page,
        sort: this.filters.sort,
        sort_dir: this.filters.sort_dir,
        stock_filter: this.filters.stock_filter,
      };
      if (expandGuid) {
        payload.expand_group_guid = expandGuid;
        this.dashboardExpandLoading = true;
      }
      try {
        const data = await FinancaStockService.getDashboard(payload);
        this.dashboardData = { groups: data.groups || [], totals: data.totals || {} };
        this.dashboardExpanded = data.expanded || null;
        this.lastSync = data.last_sync || this.lastSync;
        if (data.value_source) this.valueSourceFromApi = data.value_source;
      } finally {
        this.dashboardExpandLoading = false;
      }
    },

    async toggleDashboardExpand(group) {
      if (!group || !group.guid) return;
      if (this.dashboardExpandGuid === group.guid) {
        this.dashboardExpandGuid = null;
        this.dashboardExpanded = null;
        return;
      }
      this.dashboardExpandGuid = group.guid;
      this.filters = { ...this.filters, page: 1 };
      await this.loadDashboard(group.guid);
    },

    onDashboardExpandPage(page) {
      this.filters = { ...this.filters, page };
      this.loadDashboard(this.dashboardExpandGuid);
    },

    onDashboardExpandPerPage(perPage) {
      this.filters = { ...this.filters, per_page: perPage, page: 1 };
      this.loadDashboard(this.dashboardExpandGuid);
    },

    normalize(data) {
      const rows = data.rows || [];
      return {
        columns: data.columns || [],
        rows,
        totals: data.totals || {},
        pagination: {
          page: 1,
          per_page: this.filters.per_page,
          total: null,
          last_page: null,
          has_more: false,
          ...(data.pagination || {}),
          row_count: rows.length,
        },
      };
    },

    reload() {
      this.lastPayloadKey = null;
      this.loadStatus();
      this.load();
    },

    switchTab(tab) {
      if (this.tab === tab) return;
      this.tab = tab;
      this.dashboardExpandGuid = null;
      this.dashboardExpanded = null;
      this.lastPayloadKey = null;
      this.filters = { ...this.filters, page: 1 };
      if (tab === "dashboard") this.load();
    },

    changePage(page) {
      this.filters = { ...this.filters, page };
    },

    changePerPage(perPage) {
      this.filters = { ...this.filters, per_page: perPage, page: 1 };
    },

    changeSort(sort) {
      const sortDir = this.filters.sort === sort && this.filters.sort_dir === "asc" ? "desc" : "asc";
      this.filters = { ...this.filters, sort, sort_dir: sortDir, page: 1 };
    },

    onRangeChange(range) {
      this.range = range;
      this.lastPayloadKey = null;
      this.filters = { ...this.filters, page: 1 };
    },

    onDirectionChange(direction) {
      this.direction = direction;
      this.lastPayloadKey = null;
      this.filters = { ...this.filters, page: 1 };
    },

    onMinIncreaseChange(value) {
      this.filters = { ...this.filters, min_increase: value, page: 1 };
    },

    openHistory(row) {
      if (!this.canSeeMovements) return;
      this.historyProduct = row;
    },

    async openProductKartela(row) {
      if (!this.canEditProduct) {
        if (this.$toast) this.$toast.error("Nuk keni të drejtë të modifikoni produktin.");
        return;
      }
      if (!row || !row.guid) {
        this.notify("Mungon GUID i produktit.");
        return;
      }
      this.historyProduct = null;
      this.formEdit = { loading: true };
      try {
        this.formEdit = await editProduct({ guid: row.guid });
      } catch (error) {
        this.formEdit = null;
        this.notify(this.errorText(error));
      }
    },

    closeProductKartela() {
      this.formEdit = null;
      this.productSaving = false;
    },

    async submitProductKartela(payload, closeOnSubmit = true, onAfterCreate = null, onDone = null) {
      void onAfterCreate;
      if (this.productSaving) return;
      this.productSaving = true;
      try {
        const guid = this.formEdit?.guid || payload?.guid;
        if (!guid) throw new Error("Nuk u gjet GUID i produktit.");
        await ProductsServices.editProduct({ ...payload, guid });
        if (this.$toast) this.$toast.success("Produkti u modifikua me sukses.");
        if (closeOnSubmit) this.closeProductKartela();
      } catch (error) {
        this.notify(this.errorText(error));
      } finally {
        this.productSaving = false;
        if (typeof onDone === "function") onDone();
      }
    },

    applyImport({ type, guids, matched }) {
      this.showImportModal = false;
      if (!guids.length) return;

      if (type === "warehouse") {
        this.notify(matched.length + " magazina u zgjodhën nga skedari.", "info");
        this.filters = { ...this.filters, group_by: "warehouse", warehouse_ids: guids, pos_ids: [], page: 1 };
      } else {
        this.notify(matched.length + " produkte u zgjodhën nga skedari.", "info");
        this.filters = { ...this.filters, product_guids: guids, page: 1 };
      }
    },

    async exportCurrentView() {
      this.exporting = true;
      try {
        const payload = { ...this.filters, page: 1, per_page: this.filters.per_page };
        let response;
        let filename = "gjendja_analitike.csv";
        if (this.tab === "supply") {
          response = await FinancaStockService.exportMovements({ ...payload, from: this.range.from, to: this.range.to, direction: this.direction });
          filename = "furnizime_analitike.csv";
        } else if (this.tab === "dashboard") {
          response = await FinancaStockService.exportStock({ ...payload, group_by: "product", metric: this.filters.metric || "both" });
          filename = "permbledhje_analitike.csv";
        } else {
          response = await FinancaStockService.exportStock(payload);
        }

        FinancaStockService.saveBlobDownload(response, filename);
      } catch (error) {
        this.notify(await this.blobErrorText(error));
      } finally {
        this.exporting = false;
      }
    },

    async blobErrorText(error) {
      const data = error && error.response && error.response.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed && parsed.message) return parsed.message;
        } catch (_) {
          /* ignore invalid JSON in blob error body */
        }
      }
      return this.errorText(error);
    },

    async syncNow() {
      this.syncing = true;
      try {
        const result = await FinancaStockService.syncNow();
        this.lastSync = result.last_sync || this.lastSync;
        await this.loadStatus();
        const matched = Number(result.products_matched || 0);
        const fetched = Number(result.products_fetched || 0);
        if (fetched) {
          this.notify(matched + " nga " + fetched + " produkte u përputhën. Gjendja u rifreskua; furnizimet mbyllen në mbrëmje.", "info");
        } else {
          this.notify("Gjendja u rifreskua nga Financa.", "info");
        }
        this.lastPayloadKey = null;
        await this.load();
      } catch (error) {
        this.notify(this.errorText(error));
      } finally {
        this.syncing = false;
      }
    },

    errorText(error) {
      return (error && error.response && error.response.data && error.response.data.message) || (error && error.message) || "Ndodhi një gabim gjatë komunikimit me serverin.";
    },
  },
};
</script>
